import * as React from 'react'
import { Link } from 'react-router-dom'

import { PageMeta, DocSection, StepList, KVTable, Callout, Flow, Tag } from '@/components/docs/primitives'
import { P, UL } from '@/components/docs/prose'
import { brand } from '@/brand/brand'

export function GettingStarted() {
  return (
    <>
      <PageMeta
        title={`Welcome to ${brand.productName}`}
        summary={`${brand.productName} is an AI ad creative studio for consumer product brands. It generates short-form video ads and hero images for your product line — Novera Body Oil, Novera Radiance Serum, Novera Hair Revival Kit and the rest — ready for Instagram, TikTok, and Reels.`}
      />

      <DocSection id="what-this-is" title="What this tool does">
        <P>
          {brand.productName} is an ad studio built around a single brief: produce on-brand
          creative for your product line. It knows your products, visual palette, and brand
          voice. You describe a concept in one sentence, pick a style, and the system writes the
          scene instructions, renders the footage, synthesises a voice if needed, and assembles
          the clips. No timeline editing, no generalist prompt engineering.
        </P>
        <P>
          This is not a generic AI video tool pointed at a brand brief. The templates, bias
          blocks, and forbidden-word lists wired into every generation run are specific to your
          brand. UGC mode knows to avoid the words that push output toward a stock AI-ad
          aesthetic. Cinematic mode knows your botanical palette — rosehip petals, amber glass,
          gold embossing, warm neutrals. That specificity is what makes the output feel like your
          brand rather than a competitor's ad.
        </P>
      </DocSection>

      <DocSection id="where-to-start" title="Where to start">
        <StepList
          items={[
            {
              title: 'Products',
              body: 'Make sure the product you want to advertise exists in your product library with its name, benefits, ingredients, and at least one hero image. If it is not there yet, paste your product page URL into the scrape field and the system pulls the copy and images for you.',
            },
            {
              title: 'Reference images',
              body: "Ideally upload three angles of each product: front-on, three-quarter view, and a label close-up. Your gold-embossed labels on amber glass are distinctive — the more angles the AI sees, the better it reconstructs the real label text instead of inventing it. This is the single biggest quality lever for every rendered scene.",
            },
            {
              title: 'Create Ad',
              body: 'Pick avatar mode for a UGC testimonial-style video with a speaking spokesperson, or product mode for an oil-pour, still-life, or botanical scene with no person. Type one sentence of concept, pick a video style, hit Generate. The AI writes a 2-to-4-scene script in your brand voice and renders each scene in the background.',
            },
            {
              title: 'Review in Jobs',
              body: 'The exact instructions sent to the AI are saved for every render. If the output drifts from what you expected, open the job, read the built prompt, and see what shifted. Copy the seed from a render you liked to get similar motion on the next run.',
            },
          ]}
        />
        <P>
          Full reference for each area:{' '}
          <Link to="/docs/products">
            <Tag variant="page">Products</Tag>
          </Link>
          {' — '}
          <Link to="/docs/assets">
            <Tag variant="page">Saved Context</Tag>
          </Link>
          {' — '}
          <Link to="/docs/create-ad">
            <Tag variant="page">Create Ad</Tag>
          </Link>
          {' — '}
          <Link to="/docs/jobs">
            <Tag variant="page">Created Assets</Tag>
          </Link>
        </P>
      </DocSection>

      <DocSection id="video-styles" title="The four video styles and when to use each">
        <P>
          Video style controls the mood of every scene prompt the AI writes. Changing it changes
          which words the AI is instructed to use and which it is explicitly told to avoid. Pick
          the style that matches the feeling of the ad you are making, not the product.
        </P>
        <UL>
          <li>
            <strong>UGC</strong> — Handheld phone-camera feel, casual authentic energy, soft
            daylight, natural blinking, realistic skin texture. Use this when you want a senior
            customer talking about Novera Body Oil with a kitchen window behind her, or a creator
            unboxing the Hair Revival Kit. The bias block for this style explicitly forbids words
            like "cinematic", "golden hour", and "studio lighting" because those push the output
            toward a polished AI-ad aesthetic rather than a genuine creator clip.
          </li>
          <li>
            <strong>Cinematic</strong> — Slow dolly-in, shallow depth of field, explicit lighting
            specificity, luxury commercial aesthetics. Use this for a Radiance Serum bottle
            rotating on obsidian with rosehip petals drifting through a backlit mist, or a Novera
            Body Oil pour in slow motion on warm marble. The look you'd shoot in a professional
            studio, rendered by AI.
          </li>
          <li>
            <strong>Lifestyle</strong> — Natural window light, editorial framing, gentle push-ins,
            authentic human moments. Use this for hands pouring Shower Oil over skin in a warm
            bathroom, or someone pressing Radiance Serum into their cheeks by an open window. The
            tone is organic and intimate rather than polished and commercial.
          </li>
          <li>
            <strong>Hype Retail</strong> — Hard rim light with coloured gels, snappy cuts, bold
            DTC energy. Use this for a Hair Revival Kit or Novera Smooth Kit reveal — the kind of
            creative that stops the scroll with quick cuts and high-contrast colour.
          </li>
        </UL>
      </DocSection>

      <DocSection id="the-brand" title="How to think about your ads">
        <P>
          Your brand sits at the intersection of botanical heritage and modern clean beauty.
          The language that works is ethereal yet grounded: "feminine wisdom", "ancient family
          secrets", "alchemy", paired with ingredient transparency and precise visual references —
          rosehip, sweet almond, evening primrose, patchouli petals, amber glass, gold embossing.
          The visual palette is warm neutrals, soft whites, gold accents, and sensorial close-ups
          of oil textures and botanicals. Every ad should feel like something that belongs in
          your brand world, not like a competitor's creative mislabelled.
        </P>
        <P>
          When writing scene concepts or editing AI-generated scripts, keep those reference points
          close. A Radiance Serum scene at golden hour on obsidian with a rosehip close-up is
          on-brand. The same product on a stark white background under studio flash with a
          clinical voiceover is not. The visual and verbal vocabulary matters more than technical
          perfection in the render.
        </P>
        <P>
          For avatar-mode ads, the spokesperson persona should match your customer: warmth,
          lived experience, trust. A senior woman who has used Novera Body Oil for years is more
          credible than a young influencer-type rattling through bullet points. The tone should
          be intimate and genuine, never high-pressure.
        </P>
        <Callout variant="tip">
          If a prompt uses words like "corporate", "sterile", "TV-ad", or "loud", you have
          drifted. Your brand is warm, sensorial, and heritage. Rewrite before generating.
        </Callout>
      </DocSection>

      <DocSection id="quick-glossary" title="A few terms you will see">
        <KVTable
          rows={[
            {
              key: 'Scene prompt',
              value:
                'The 60-to-100-word description of a single scene that the AI turns into video. Written by Gemini following the 6-step formula, then passed to Seedance for rendering.',
            },
            {
              key: 'Reference image',
              value:
                'A photo the AI tries to preserve in the rendered output — the shape, label text, and colour of the real product bottle. Three consistent angles of the same product bottle gives the best label accuracy.',
            },
            {
              key: 'Video style',
              value:
                'One of UGC, Cinematic, Lifestyle, or Hype Retail. Determines the vocabulary the AI uses when writing scene prompts — and, critically, the words it is forbidden to use.',
            },
            {
              key: 'Built prompt',
              value:
                'The final text sent to the AI after all templates, bias blocks, and product context are assembled. Visible on every job. Reading it is the fastest way to debug unexpected output.',
            },
            {
              key: 'Seed',
              value:
                'A number that makes renders reproducible. Copy the seed from a completed job and use it on the next run to get similar motion and framing — useful when iterating on a scene you almost like.',
            },
            {
              key: 'Constraint clause',
              value:
                'The short phrase at the end of every scene prompt — "avoid jitter, avoid bent limbs, avoid temporal flicker" — that tells the AI to steer away from its most common failure modes.',
            },
          ]}
        />
      </DocSection>
    </>
  )
}
